/**
 * 페이지별 SEO 메타 정의.
 *
 * 키워드 맵의 SSOT 는 `claudeFiles/judahsWeb/SEO_PLAN.md` 다.
 * 여기 문구를 고칠 때는 그 문서의 표도 함께 갱신한다.
 *
 * title 은 60자, description 은 155자 안쪽을 목표로 한다.
 * 그 이상은 검색 결과에서 잘린다.
 */

export interface SeoEntryType {
	/** 라우트 경로. canonical 생성에 쓴다. */
	path: string
	title: string
	description: string
	/** 지정하지 않으면 사이트 기본 OG 이미지 */
	ogImage?: string
	/** true 면 robots 에 noindex 를 넣는다 */
	noindex?: boolean
}

export const SEO_SITE = {
	origin: 'https://judahs.io',
	siteName: 'Judahs',
	defaultOgImage: 'https://judahs.io/logo.png',
	locale: 'en_US',
	/** 홈에서 쓰는 기본 문구. 개별 항목이 없는 경로의 fallback 이기도 하다. */
	defaultTitle: 'Judahs — MIDI Controllers & Loop Switchers for Guitar',
	defaultDescription:
		'Judahs builds MIDI foot controllers and programmable loop switchers for guitar pedalboards — the Kedem controller line and the Koinos loop switcher line.',
} as const

/**
 * 경로 → 메타.
 *
 * `/main`, `/admin` 하위(백오피스)는 여기에 넣지 않는다. 색인 대상이 아니고,
 * 아래 `getSeoEntry` 가 noindex 로 처리한다.
 */
export const seoMap: { [path: string]: SeoEntryType } = {
	'/': {
		path: '/',
		title: SEO_SITE.defaultTitle,
		description:
			'Touchscreen MIDI foot controllers with wireless MIDI, and programmable loop switchers with crosspoint matrix routing. Built for working guitarists.',
	},

	'/shop': {
		path: '/shop',
		title: 'Shop — MIDI Controllers & Loop Switchers | Judahs',
		description:
			'The full Judahs lineup: Kedem 6 and Kedem 4 MIDI controllers, AirMIDI POG 2 wireless control, Dual Switch, and the Koinos PLS 8 loop switcher.',
	},

	'/shop/kdm6': {
		path: '/shop/kdm6',
		title: 'Kedem 6 — Touchscreen MIDI Controller with Wireless MIDI | Judahs',
		description:
			'Six-switch MIDI foot controller with a 3.5" touch display, AirMIDI wireless, and expression pedal input. Controls Quad Cortex, Kemper, Strymon and more.',
	},

	'/shop/kdm4': {
		path: '/shop/kdm4',
		title: 'Kedem 4 — Compact Touchscreen MIDI Controller | Judahs',
		description:
			'The compact four-switch Kedem. Same touch display and AirMIDI wireless as the Kedem 6, sized for pedalboards that have run out of room.',
	},

	'/shop/amp2': {
		path: '/shop/amp2',
		title: 'AirMIDI POG 2 — Wireless MIDI Control for the EHX POG2 | Judahs',
		description:
			'Preset switching for the Electro-Harmonix POG2 over wireless MIDI. Existing POG2 MIDI mods are wired; this one is not. Installation service available.',
	},

	'/shop/ds': {
		path: '/shop/ds',
		title: 'Judahs Dual Switch — TRS Momentary Footswitch | Judahs',
		description:
			'Two momentary footswitches over a single TRS cable. Adds external footswitch control to pedals and amps that expose a TRS jack.',
	},

	'/shop/pls8': {
		path: '/shop/pls8',
		title: 'Koinos PLS 8 — Touchscreen Loop Switcher | Judahs',
		description:
			'Eight true-bypass loops and a buffered insert on a crosspoint matrix. An 8.88-inch touch display on the unit — and input impedance you choose.',
	},

	'/about': {
		path: '/about',
		title: 'About Judahs — Guitar Effect Pedal Builders',
		description:
			'Judahs designs and builds MIDI controllers, loop switchers and wireless MIDI hardware for guitarists.',
	},

	'/contact': {
		path: '/contact',
		title: 'Contact Judahs',
		description:
			'Questions about a Judahs product, an order, or the AirMIDI installation service? Get in touch.',
	},

	'/support': {
		path: '/support',
		title: 'Support — Manuals, Firmware & Help | Judahs',
		description:
			'Manuals, firmware updates and setup help for Kedem 6, Kedem 4, AirMIDI POG 2, Dual Switch and Koinos PLS 8.',
	},

	'/manuals': {
		path: '/manuals',
		title: 'Manuals — Kedem 6, Kedem 4, Koinos PLS 8 | Judahs',
		description: 'Download user manuals for every Judahs product, in English and Korean.',
	},

	'/privacy-policy': {
		path: '/privacy-policy',
		title: 'Privacy Policy | Judahs',
		description: 'How Judahs collects, uses, stores and deletes personal information.',
	},

	'/store-policy': {
		path: '/store-policy',
		title: 'Store Policy — Shipping, Returns & Refunds | Judahs',
		description:
			'Shipping, returns, refunds and warranty terms for orders placed on judahs.io.',
	},

	'/event': {
		path: '/event',
		title: 'Events | Judahs',
		description: 'Judahs events and promotions.',
	},
}

/** 색인에서 빼는 경로. 개인화 화면과 백오피스. */
const NOINDEX_PREFIXES = ['/main', '/admin', '/cart', '/payment', '/customer', '/auth']

const isNoindexPath = (pathname: string): boolean =>
	NOINDEX_PREFIXES.some(
		prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
	)

/**
 * 현재 경로의 메타를 돌려준다.
 *
 * 맵에 없는 경로는 fallback 을 주되 반드시 `noindex` 를 붙인다.
 * CloudFront 가 없는 경로까지 200 + index.html 로 응답하기 때문에(soft 404),
 * 이 표시가 없으면 존재하지 않는 URL 이 색인된다.
 */
export const getSeoEntry = (pathname: string): SeoEntryType => {
	// 끝 슬래시는 무시하고 찾는다. `/shop/` 와 `/shop` 은 같은 페이지다.
	const normalized =
		pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

	const found = seoMap[normalized]
	if (found !== undefined) {
		return found
	}

	return {
		path: normalized,
		title: SEO_SITE.defaultTitle,
		description: SEO_SITE.defaultDescription,
		noindex: true,
	}
}

export { isNoindexPath }
